import { useCallback, useEffect, useRef, useState } from "react";

interface OnDemand<T> {
	data: T | undefined;
	loading: boolean;
	/** Fetches on the first call; later calls resolve with what is already loaded. */
	load: () => Promise<T | undefined>;
}

/**
 * Data a screen needs only for one action — the user list behind a dropdown in a modal, say — so
 * it is fetched the first time it is needed instead of on every page open. It is fetched at most
 * once per mount, which keeps it as fresh as loading it on open did.
 */
const useOnDemand = <T>(fetcher: () => Promise<T>): OnDemand<T> => {
	const [data, setData] = useState<T>();
	const [loading, setLoading] = useState(false);
	const request = useRef<Promise<T | undefined>>();
	const mounted = useRef(true);

	// The fetcher closes over render-time values; always call the latest one.
	const fetcherRef = useRef(fetcher);
	fetcherRef.current = fetcher;

	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);

	const load = useCallback(() => {
		if (!request.current) {
			setLoading(true);
			request.current = fetcherRef
				.current()
				.then((result) => {
					if (mounted.current) setData(result);
					return result;
				})
				.catch((error) => {
					console.error(error);
					// Let the next call try again.
					request.current = undefined;
					return undefined;
				})
				.finally(() => {
					if (mounted.current) setLoading(false);
				});
		}
		return request.current;
	}, []);

	return { data, loading, load };
};

export default useOnDemand;
